import React, { useState, useEffect, useMemo, useRef } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faPen, faSort, faSortUp, faSortDown, faCheck } from "@fortawesome/free-solid-svg-icons";
import { faCopy } from "@fortawesome/free-regular-svg-icons";
import { formatCrmDateTime } from "../../utils/date";
import OlympicStatusTeamLeaderCell from "./OlympicStatusTeamLeaderCell";
import "../css/ParticipatingOrganisationList.css";
import "../css/StatusLosAngeles2028List.css";
import "../css/EmailCopyControl.css";
import "../css/PersonList.css";
import "../css/ParticipantList.css";

const COLUMN_COUNT = 10;

function StatusLosAngeles2028List() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [rowsPerPage, setRowsPerPage] = useState(100);
  const [page, setPage] = useState(1);
  const [totalRows, setTotalRows] = useState(0);
  const [sortConfig, setSortConfig] = useState({ key: "updatedAt", direction: "desc" });
  const [showToast, setShowToast] = useState(false);
  const [toastMessage, setToastMessage] = useState("");

  const navigate = useNavigate();
  const API = process.env.REACT_APP_API_URL;
  const fetchGenRef = useRef(0);

  useEffect(() => {
    const timer = setTimeout(() => setDebouncedSearch(searchTerm.trim()), 300);
    return () => clearTimeout(timer);
  }, [searchTerm]);

  useEffect(() => {
    const controller = new AbortController();
    const gen = ++fetchGenRef.current;

    const fetchRows = async () => {
      setLoading(true);
      try {
        const res = await axios.get(`${API}/organisations/status-la-2028`, {
          params: {
            page,
            limit: rowsPerPage,
            search: debouncedSearch || undefined,
            sortBy: sortConfig.key || undefined,
            sortDir: sortConfig.direction || undefined,
          },
          signal: controller.signal,
        });
        if (gen !== fetchGenRef.current) return;
        setRows(res.data.data || []);
        setTotalRows(res.data.total || 0);
      } catch (error) {
        if (controller.signal.aborted || error.code === "ERR_CANCELED") return;
        if (gen !== fetchGenRef.current) return;
        console.error("Error fetching Status Los Angeles 2028 organisations:", error);
        setRows([]);
        setTotalRows(0);
      } finally {
        if (gen === fetchGenRef.current) {
          setLoading(false);
        }
      }
    };

    fetchRows();
    return () => controller.abort();
  }, [API, page, rowsPerPage, debouncedSearch, sortConfig.key, sortConfig.direction]);

  useEffect(() => {
    setPage(1);
  }, [debouncedSearch, rowsPerPage, sortConfig.key, sortConfig.direction]);

  const toggleSort = (key) => {
    setSortConfig((prev) => ({
      key,
      direction:
        prev.key === key ? (prev.direction === "asc" ? "desc" : "asc") : "asc",
    }));
  };

  const sortIcon = (key) => {
    if (sortConfig.key !== key) return faSort;
    return sortConfig.direction === "asc" ? faSortUp : faSortDown;
  };

  const SortableHeader = ({ label, sortKey, center = false }) => (
    <th className={center ? "status-la2028-th-center" : undefined}>
      <button
        type="button"
        className={`sort-header status-la2028-sort-header ${sortConfig.key === sortKey ? "sorted" : ""}`}
        onClick={() => toggleSort(sortKey)}
      >
        <span>{label}</span>
        <FontAwesomeIcon icon={sortIcon(sortKey)} className="sort-icon" />
      </button>
    </th>
  );

  const totalPages = Math.max(Math.ceil(totalRows / rowsPerPage), 1);
  const serialOffset = (page - 1) * rowsPerPage;

  const pageNumbers = useMemo(() => {
    const maxButtons = 7;
    if (totalPages <= maxButtons) {
      return Array.from({ length: totalPages }, (_, i) => i + 1);
    }
    const start = Math.max(1, Math.min(page - 3, totalPages - maxButtons + 1));
    return Array.from({ length: maxButtons }, (_, i) => start + i);
  }, [page, totalPages]);

  const copyEmails = (text) => {
    const value = String(text || "").trim();
    if (!value || value === "-") return;
    navigator.clipboard.writeText(value);
    setToastMessage("Emails copied to clipboard.");
    setShowToast(false);
    setTimeout(() => setShowToast(true), 10);
    setTimeout(() => setShowToast(false), 3000);
  };

  const displayLeaderName = (value) => {
    if (value === 0 || value === "0") return "-";
    if (value == null || value === "") return "-";
    return value;
  };

  const displayLeaderEntries = (display, fallback) => {
    if (Array.isArray(display) && display.length) return display;
    const value = displayLeaderName(fallback);
    if (value === "-") return [];
    return [{ name: value, indicator: null }];
  };

  return (
    <div className="participating-orgs-list status-la2028-page">
      <h1 className="status-la2028-page-title">Status Los Angeles 2028</h1>

      <div className="d-flex justify-content-between align-items-center mb-3 status-la2028-toolbar">
        <div className="status-la2028-search-wrap">
          <input
            type="text"
            placeholder="Search Organisations"
            className="form-control search-input"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </div>

        <div className="show-section">
          <span>Show</span>
          <select
            value={rowsPerPage}
            onChange={(e) => setRowsPerPage(Number(e.target.value))}
            className="show-dropdown-select"
          >
            <option value={10}>10</option>
            <option value={25}>25</option>
            <option value={50}>50</option>
            <option value={100}>100</option>
          </select>
        </div>
      </div>

      <div className="org-table-container status-la2028-table-container">
        <table className="org-table status-la2028-table">
          <thead>
            <tr className="org-header">
              <SortableHeader label="#" sortKey="serial" center />
              <th>Logo</th>
              <SortableHeader label="Country" sortKey="country" />
              <SortableHeader label="Name" sortKey="name" />
              <SortableHeader label="No. of Team Members" sortKey="teamMembers" center />
              <SortableHeader label="Emails" sortKey="emails" center />
              <SortableHeader label="TL Men" sortKey="tlMen" center />
              <SortableHeader label="TL Women" sortKey="tlWomen" center />
              <SortableHeader label="Last Updated" sortKey="updatedAt" />
              <th className="status-la2028-th-center">Action</th>
            </tr>
          </thead>

          <tbody>
            {loading ? (
              <tr>
                <td colSpan={COLUMN_COUNT} className="text-center p-3">
                  Loading...
                </td>
              </tr>
            ) : rows.length === 0 ? (
              <tr>
                <td colSpan={COLUMN_COUNT} className="text-center p-3 text-muted">
                  No Los Angeles 2028 organisations found
                </td>
              </tr>
            ) : (
              rows.map((row, index) => (
                <tr className="org-row" key={row._id}>
                  <td className="status-la2028-serial">{serialOffset + index + 1}</td>
                  <td>
                    <img
                      src={row.logo ? `/images/${row.logo}` : "/images/org_logo1.png"}
                      alt={row.name || "Logo"}
                      style={{ width: 40, height: 40, borderRadius: 6, objectFit: "contain" }}
                    />
                  </td>
                  <td>{row.countryCode || "-"}</td>
                  <td
                    className="org-name"
                    onClick={() =>
                      navigate(`/organisation/${row._id}`, {
                        state: { organisation: row },
                      })
                    }
                  >
                    {row.name || "Unnamed Organisation"}
                  </td>
                  <td className="status-la2028-center">{row.teamMembers ?? 0}</td>
                  <td>
                    <div className="orgns-email-copy-cell status-la2028-emails">
                      <span className="orgns-email-text">{row.emails ?? "-"}</span>
                      {row.emails && row.emails !== "-" && (
                        <button
                          type="button"
                          className="orgns-email-copy-btn"
                          title="Copy emails"
                          onClick={(e) => {
                            e.stopPropagation();
                            copyEmails(row.emails);
                          }}
                        >
                          <FontAwesomeIcon icon={faCopy} style={{ color: "#D9A128" }} />
                        </button>
                      )}
                    </div>
                  </td>
                  <td className="status-la2028-center">
                    <OlympicStatusTeamLeaderCell entries={displayLeaderEntries(row.tlMenDisplay, row.tlMen)} />
                  </td>
                  <td className="status-la2028-center">
                    <OlympicStatusTeamLeaderCell entries={displayLeaderEntries(row.tlWomenDisplay, row.tlWomen)} />
                  </td>
                  <td>{formatCrmDateTime(row.updatedAt)}</td>
                  <td>
                    <div className="d-flex justify-content-center">
                      <button
                        type="button"
                        className="icon-btn"
                        title="Edit organisation"
                        onClick={() =>
                          navigate(`/organisation/${row._id}`, {
                            state: { organisation: row },
                          })
                        }
                      >
                        <FontAwesomeIcon icon={faPen} />
                      </button>
                    </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>

        <div className="participant-pagination status-la2028-pagination">
          <div className="status-la2028-pagination-summary">
            {totalRows === 0
              ? "0 organisations"
              : `${serialOffset + 1}–${Math.min(serialOffset + rows.length, totalRows)} of ${totalRows}`}
          </div>

          <div className="pagination-box">
            <button
              type="button"
              className="pagination-btn"
              disabled={page === 1 || loading}
              onClick={() => setPage((p) => Math.max(p - 1, 1))}
            >
              Previous
            </button>

            {pageNumbers.map((num) => (
              <button
                key={num}
                type="button"
                className={`pagination-btn ${num === page ? "active" : ""}`}
                disabled={loading}
                onClick={() => setPage(num)}
              >
                {num}
              </button>
            ))}

            <button
              type="button"
              className="pagination-btn"
              disabled={page >= totalPages || loading || totalRows === 0}
              onClick={() => setPage((p) => Math.min(p + 1, totalPages))}
            >
              Next
            </button>
          </div>
        </div>
      </div>

      {showToast && (
        <div className="copy-toast">
          <FontAwesomeIcon icon={faCheck} style={{ marginRight: "10px", color: "#D9A128" }} />
          {toastMessage}
        </div>
      )}
    </div>
  );
}

export default StatusLosAngeles2028List;
